import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { TrendingUp, CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';

const ProficiencySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const levels = [
    {
      level: "Emerging",
      icon: <HelpCircle className="w-8 h-8 text-primary" />,
      description: "Beginning to develop understanding and skills",
      color: "bg-primary/10"
    },
    {
      level: "Developing",
      icon: <AlertCircle className="w-8 h-8 text-primary" />,
      description: "Shows some understanding and application",
      color: "bg-primary/20"
    },
    {
      level: "Proficient",
      icon: <CheckCircle className="w-8 h-8 text-primary" />,
      description: "Demonstrates expected understanding and skills",
      color: "bg-primary/30"
    },
    {
      level: "Extending",
      icon: <TrendingUp className="w-8 h-8 text-primary" />,
      description: "Exceeds expectations with deep understanding",
      color: "bg-primary/40"
    }
  ];

  return (
    <section id="proficiency" className="py-20 bg-cream" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-primary mb-4">
            Proficiency Scale
          </h2>
          <p className="text-xl text-primary/80 max-w-3xl mx-auto">
            It describes how well a student meets learning goals, typically ranging from Emerging to Extending. It shifts the focus from grades to learning progress, helping educators give clearer, skill-based feedback.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {levels.map((level, index) => (
            <motion.div
              key={level.level}
              className="bg-secondary/20 backdrop-blur-sm rounded-2xl p-8 border border-secondary/30"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className={`${level.color} rounded-full p-3`}>
                  {level.icon}
                </div>
                <h3 className="text-2xl font-semibold text-primary">
                  {level.level}
                </h3>
              </div>
              <p className="text-primary/80">
                {level.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-16 bg-primary rounded-3xl p-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-3xl font-bold text-secondary mb-8">
            Moving Beyond Traditional Grades
          </h3>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-cream mb-2">4</div>
              <div className="text-secondary/80">Proficiency Levels</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-cream mb-2">100%</div>
              <div className="text-secondary/80">Clear Communication</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-cream mb-2">∞</div>
              <div className="text-secondary/80">Growth Potential</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProficiencySection;
